import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {first, Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {ICompanyDashboard, ICompanyDetails, ICompanyDetailsSimplified, ICompanyListPaged, ICompanyRemovalEligibility, IExpertClientDetail} from '@models/company.interface';
import {getDefaultHttpHeaders} from '../utils/http-headers.utils';
import {ICompanyDocument, ICompteAdministration, IPasswordUpdateRequest} from '@models/compteAdministration.interface';
import {
  IBodaccAlert,
  IBodaccSyncResult,
  ICompanyContactUpdate,
  ICompanyKbis,
  ICompanyNotification,
  IDocumentRetrievalAlert,
  ICompanyRibFile,
  ICompanySettings,
  ICompanyCertification,
  ICompanyUserFile,
  ITransmitItem,
  IWatchedCompany
} from '@models/workspace.interface';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {

  BASE_URL = "/company";
  FILE_URL = "/file";

  constructor(private http: HttpClient) {}

  createCompanyWithoutExternalAccount(siret: string): Observable<ICompteAdministration> {
    return this.http.post<ICompteAdministration>(`${environment.apiUrl}${this.BASE_URL}/${siret}`, { headers: getDefaultHttpHeaders().headers }).pipe(first());
  }

  removeCompanyForUser(siret: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  getCompanyRemovalEligibility(siret: string): Observable<ICompanyRemovalEligibility> {
    return this.http.get<ICompanyRemovalEligibility>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/removal-eligibility`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  getCompanies(): Observable<ICompanyDetailsSimplified[]> {
    return this.http.get<ICompanyDetailsSimplified[]>(`${environment.apiUrl}${this.BASE_URL}/list`, { headers: getDefaultHttpHeaders().headers }).pipe(first());
  }

  getCompaniesPaged(page: number, size: number, q?: string): Observable<ICompanyListPaged> {
    const params: Record<string, string> = {page: String(page), size: String(size)};
    const trimmed = q?.trim();
    if (trimmed) {
      params['q'] = trimmed;
    }
    return this.http.get<ICompanyListPaged>(`${environment.apiUrl}${this.BASE_URL}/list/paged`, {
      headers: getDefaultHttpHeaders().headers,
      params,
    }).pipe(first());
  }

  getExpertClientDetail(siret: string): Observable<IExpertClientDetail> {
    return this.http
      .get<IExpertClientDetail>(`${environment.apiUrl}${this.BASE_URL}/${siret}/expert-detail`, {headers: getDefaultHttpHeaders().headers})
      .pipe(first());
  }

  revealImpotsPassword(siret: string): Observable<{password: string}> {
    return this.http
      .get<{password: string}>(`${environment.apiUrl}${this.BASE_URL}/${siret}/impots-password/reveal`, {headers: getDefaultHttpHeaders().headers})
      .pipe(first());
  }

  getCompanyBySiret(siret: string): Observable<ICompteAdministration> {
    return this.http.get<ICompteAdministration>(`${environment.apiUrl}${this.BASE_URL}/${siret}`, { headers: getDefaultHttpHeaders().headers }).pipe(first());
  }

  getDashboard(siret: string): Observable<ICompanyDashboard> {
    return this.http.get<ICompanyDashboard>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/dashboard`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  pushWalletUpdate(siret: string): Observable<void> {
    return this.http.post<void>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/wallet-push`,
      {},
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  updatePasswords(siret: string, request: IPasswordUpdateRequest): Observable<ICompteAdministration> {
    return this.http.put<ICompteAdministration>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/passwords`,
      request,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  deleteCredentials(siret: string, service: string): Observable<ICompteAdministration> {
    return this.http.delete<ICompteAdministration>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/credentials/${service}`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  uploadExcel(file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<void>(
      `${environment.apiUrl}${this.FILE_URL}/upload`,
      formData
    ).pipe(first());
  }

  downloadImportTemplate(): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}${this.FILE_URL}/import-template`,
      {responseType: 'blob', headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  downloadDocumentForAll(documentType: string): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}${this.BASE_URL}/download-all/${documentType}`, {
      responseType: 'blob',
      headers: getDefaultHttpHeaders().headers,
    }).pipe(first());
  }

  getDocuments(siret: string): Observable<ICompanyDocument[]> {
    return this.http.get<ICompanyDocument[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/documents`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  downloadDocument(fileId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}${this.FILE_URL}/${fileId}`,
      { responseType: 'blob' }
    ).pipe(first());
  }

  downloadAppleWalletPass(siret: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/wallet/apple`,
      { responseType: 'blob' }
    ).pipe(first());
  }

  getGoogleWalletUrl(siret: string): Observable<{ saveUrl: string }> {
    return this.http.get<{ saveUrl: string }>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/wallet/google`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  syncBodacc(): Observable<IBodaccSyncResult> {
    return this.http.post<IBodaccSyncResult>(
      `${environment.apiUrl}/bodacc/sync`,
      {},
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  getBodaccAlerts(siret: string): Observable<IBodaccAlert[]> {
    return this.http.get<IBodaccAlert[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/bodacc/alerts`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  getAllBodaccAlerts(): Observable<IBodaccAlert[]> {
    return this.http.get<IBodaccAlert[]>(
      `${environment.apiUrl}/bodacc/alerts`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  markBodaccAlertRead(alertId: string): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}/bodacc/alerts/${alertId}/read`,
      {},
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  markAllBodaccRead(siret: string): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/bodacc/mark-all-read`,
      {},
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  listUserFiles(siret: string, type?: string): Observable<ICompanyUserFile[]> {
    return this.http.get<ICompanyUserFile[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/user-files`,
      {
        headers: getDefaultHttpHeaders().headers,
        params: type ? { type } : {}
      }
    ).pipe(first());
  }

  uploadUserFile(siret: string, file: File, type?: string): Observable<ICompanyUserFile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ICompanyUserFile>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/user-files`,
      formData,
      {
        params: type ? { type } : {}
      }
    ).pipe(first());
  }

  downloadUserFile(siret: string, fileId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/user-files/${fileId}/content`,
      { responseType: 'blob', headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  deleteUserFile(siret: string, fileId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/user-files/${fileId}`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  listWatchedCompanies(siret: string): Observable<IWatchedCompany[]> {
    return this.http.get<IWatchedCompany[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/watched-companies`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  addWatchedCompany(siret: string, watchedSiren: string, companyName: string | null): Observable<IWatchedCompany> {
    return this.http.post<IWatchedCompany>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/watched-companies`,
      { watchedSiren, companyName },
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  deleteWatchedCompany(siret: string, watchedId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/watched-companies/${watchedId}`,
      { headers: getDefaultHttpHeaders().headers }
    ).pipe(first());
  }

  getKbis(siret: string): Observable<ICompanyKbis> {
    return this.http.get<ICompanyKbis>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/kbis`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  getRibFiles(siret: string): Observable<ICompanyRibFile[]> {
    return this.http.get<ICompanyRibFile[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/rib`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  getTransmitItems(siret: string): Observable<ITransmitItem[]> {
    return this.http.get<ITransmitItem[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/transmit`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  getNotifications(siret: string): Observable<ICompanyNotification[]> {
    return this.http.get<ICompanyNotification[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/notifications`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  markNotificationRead(id: string): Observable<void> {
    return this.http.put<void>(
      `${environment.apiUrl}/bodacc/alerts/${id}/read`,
      {},
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  getAllDocumentRetrievalAlerts(): Observable<IDocumentRetrievalAlert[]> {
    return this.http.get<IDocumentRetrievalAlert[]>(
      `${environment.apiUrl}/document-retrieval/alerts`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  getSettings(siret: string): Observable<ICompanySettings> {
    return this.http.get<ICompanySettings>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/settings`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  updateContact(siret: string, payload: ICompanyContactUpdate): Observable<ICompanySettings> {
    return this.http.put<ICompanySettings>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/contact`,
      payload,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  updateSettings(siret: string, payload: ICompanySettings): Observable<ICompanySettings> {
    return this.http.put<ICompanySettings>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/settings`,
      payload,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }

  getCertifications(siret: string): Observable<ICompanyCertification[]> {
    return this.http.get<ICompanyCertification[]>(
      `${environment.apiUrl}${this.BASE_URL}/${siret}/certifications`,
      {headers: getDefaultHttpHeaders().headers}
    ).pipe(first());
  }
}
